import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import ConfirmDialog, { confirmDialog } from '../../../components/ui/ConfirmDialog'
import AttachmentTable, { type AttachmentTableRow } from '../../../components/ui/AttachmentTable'
import AttachmentPreviewPanel from '../../../components/ui/AttachmentPreviewPanel'
import FileUpload from '../../../components/ui/FileUpload'
import { useToast } from '../../../components/ui/ToastProvider'
import { GRANTS } from '../../../auth/grants'
import { useAttachmentPreview } from '../../../lib/useAttachmentPreview'
import { ATTACHMENT_ACCEPT } from '../../../lib/uploadAccept'
import { usePrincipleAttachments } from '../hooks/usePrincipleDecisions'

/**
 * Pop-up'ın başlıkta göstermek için ihtiyaç duyduğu <b>asgari</b> kayıt şekli. `PrincipleDecision` bunu
 * yapısal olarak karşılar; Toplantı Detay'daki prensip kararı satırı (`MinuteItemRow`) da eşlenebilir —
 * ikisi ayrı tiplerdir ama pop-up'ın ikisinden de yalnız bu üç alana ihtiyacı var.
 */
export interface PrincipleFilesTarget {
  id: number
  recordNumber?: string | null
  description: string
}

interface Props {
  /** Açık karar; `null` → pop-up kapalı. */
  decision: PrincipleFilesTarget | null
  /** Yükleme/kaldırma yetkisi (`PRINCIPLE_DECISION_MANAGE`); false → yalnız görüntüle/indir. */
  canManage: boolean
  onHide: () => void
}

/**
 * **Prensip Kararı — Dosyalar** pop-up'ı (108). Paydaşlar pop-up'ının kardeşidir: kayıt zaten
 * oluşmuş olduğundan dosya doğrudan sunucuya gider, "kaydet"e kadar bekleyen yerel dosya yoktur.
 *
 * <p>Üç iş bir arada: <b>yükleme</b> (yetkiliyse), ortak {@link AttachmentTable} ile <b>liste</b>
 * (Dosya Adı · Tür · Boyut · Yükleyen · Yüklenme) ve satır bazında <b>Görüntüle / İndir / Kaldır</b>.
 *
 * <h2>Görüntüleme neden aynı pop-up'ta</h2>
 *
 * Kullanıcı isteği (2026-09-17): *"görüntülemeyi de popup içinde yapabilir miyiz aynı ekranda"*.
 * Dosya, indirmeyle <b>aynı</b> kimlik-doğrulamalı uçtan blob olarak çekilir ve pop-up'ın içinde
 * gösterilir; yeni sekme açılmaz, backend'e ayrı bir "inline" ucu eklenmez (gerekçe:
 * `lib/attachmentPreview.ts`). Önizleme paneli listenin <b>yerini alır</b> — yan yana koymak dar
 * ekranda iki alanı da okunmaz yapardı; "Listeye dön" ile geri dönülür.
 *
 * <p><b>Her dosya önizlenemez:</b> yalnız PDF ve görseller. Office/arşiv satırlarında göz ikonu hiç
 * çizilmez — tarayıcıda açılmayacak bir düğme sunmak, indirmeyi de gölgeler.
 */
export default function PrincipleFilesDialog({ decision, canManage, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const principleId = decision?.id ?? null
  const { list, upload, remove } = usePrincipleAttachments(principleId)

  // Önizleme durumu, object URL ömrü ve yarış koruması ORTAK hook'tadır (`useAttachmentPreview`):
  // aynı davranış Toplantı Yönetimi'nin görev dosyaları pop-up'ında da gerekiyor ve kopyalanacak olan
  // şey bir push kapısı bulgusuyla düzeltilmiş ince bir mantıktı.
  const onPreviewError = useCallback(
    () => toast.error(t('principleDecisions.files.previewError')),
    [toast, t],
  )
  const {
    preview,
    loading: previewLoading,
    open: openPreview,
    close: closePreview,
  } = useAttachmentPreview({ scopeId: principleId, onError: onPreviewError })

  const handleAdd = (files: File[]) => {
    if (files.length === 0) return
    upload.mutate(files, {
      onSuccess: () => toast.success(t('principleDecisions.files.uploaded')),
      onError: () => toast.error(t('principleDecisions.files.uploadError')),
    })
  }

  const confirmRemove = (row: AttachmentTableRow) =>
    confirmDialog({
      group: 'principle-file-remove',
      header: t('common.removeFileConfirm.title'),
      message: t('common.removeFileConfirm.message', { name: row.fileName }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        remove.mutate(Number(row.key), {
          onSuccess: () => {
            // Kaldırılan dosya o an önizlemedeyse panel de kapanmalı; aksi halde artık var olmayan
            // bir içerik ekranda kalırdı.
            closePreview()
            toast.success(t('principleDecisions.files.removed'))
          },
          onError: () => toast.error(t('principleDecisions.files.removeError')),
        }),
    })

  const rows: AttachmentTableRow[] = (list.data ?? []).map((d) => ({
    key: String(d.id),
    fileName: d.fileName,
    extension: d.extension,
    size: d.size,
    downloadId: d.id,
    uploadedByName: d.uploadedByName,
    uploadedAt: d.uploadedAt,
  }))

  const busy = upload.isPending || remove.isPending || previewLoading

  // Paydaşlar pop-up'ıyla AYNI kabuk (kullanıcı isteği 2026-09-17): ortak `FormDialog` +
  // `ui-dialog-subject` başlığı. Ham `Dialog` + `pop-subtitle` ile yazılmıştı; iki kardeş pop-up
  // yan yana açıldığında başlık tipografisi ve kenar boşlukları tutmuyordu.
  return (
    <FormDialog
      visible={decision != null}
      header={t('principleDecisions.files.title')}
      /* Footer YOK: dosya seçilir seçilmez yüklenir, "kaydet" adımı yoktur — kapanış ×/Esc ile.
         `submitLabel`/`onSubmit` yine de zorunlu prop'lar; hideFooter ikisini de etkisiz kılar. */
      hideFooter
      submitLabel=""
      onSubmit={onHide}
      onHide={onHide}
      /* Tablo beş kolonlu (ad · tür · boyut · yükleyen · yüklenme) + işlem düğmeleri; 52rem
         dosya adlarını erkenden kısaltıyordu. Önizleme paneli de bu genişlikten yararlanır. */
      width="64rem"
    >
      {decision != null && (
        <p className="ui-dialog-subject" title={decision.description}>
          {decision.recordNumber ? `${decision.recordNumber} — ` : ''}
          {decision.description}
        </p>
      )}

      {preview == null ? (
        <>
          {canManage && (
            <div className="pd-files-upload">
              <FileUpload
                files={[]}
                onChange={handleAdd}
                accept={ATTACHMENT_ACCEPT}
                label={t('principleDecisions.files.upload')}
                hint={t('principleDecisions.files.hint')}
                // Seçilen dosya anında sunucuya gider; yerleşik "bekleyen dosya" listesi yanıltıcı olurdu.
                showList={false}
              />
            </div>
          )}
          <AttachmentTable
            rows={rows}
            emptyText={t('principleDecisions.files.empty')}
            onRemove={canManage ? confirmRemove : undefined}
            onPreview={openPreview}
            busy={busy}
            requiredGrant={GRANTS.PRINCIPLE_DECISION_VIEW}
          />
        </>
      ) : (
        <AttachmentPreviewPanel preview={preview} onBack={closePreview} />
      )}

      <ConfirmDialog group="principle-file-remove" />
    </FormDialog>
  )
}
